import PropTypes from 'prop-types';
import { useState } from 'react';
import { Card, Skeleton, Icon, Collapse, Table, TableBody, TableRow, TableCell, Tooltip, useTheme } from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

const ACCENT_COLOR = '#e2001b';
const ACCENT_LIGHT = 'rgba(226,0,27,0.08)';

const ICON_BG = {
  primary: 'rgba(226,0,27,0.09)',
  secondary: 'rgba(100,116,145,0.09)',
  info: 'rgba(8,145,178,0.09)',
  success: 'rgba(31,166,120,0.09)',
  warning: 'rgba(214,139,0,0.09)',
  error: 'rgba(219,63,87,0.09)',
  dark: 'rgba(27,42,68,0.09)'
};

const ICON_COLOR = {
  primary: '#e2001b',
  secondary: '#647491',
  info: '#0891b2',
  success: '#1fa678',
  warning: '#c47d00',
  error: '#db3f57',
  dark: '#1b2a44'
};

const LEFT_BAR = {
  primary: '#e2001b',
  secondary: '#8fa0be',
  info: '#0891b2',
  success: '#1fa678',
  warning: '#c47d00',
  error: '#db3f57',
  dark: '#2a3f64'
};

function MetricCard({
  title,
  value,
  subtitle,
  icon,
  iconColor = 'info',
  loading = false,
  trend,
  placeholder = false,
  placeholderText = 'No data',
  breakdown = null,
  tooltip = ''
}) {
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const iconBg = ICON_BG[iconColor] || ICON_BG.info;
  const iconCol = ICON_COLOR[iconColor] || ICON_COLOR.info;
  const barCol = LEFT_BAR[iconColor] || LEFT_BAR.info;

  const dividerColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)';
  const dotColor = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.045)';
  const valueColor = isDark ? theme.palette.text.primary : '#0f172a';
  const titleColor = isDark ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.38)';

  const renderTrend = () => {
    if (!trend) return null;
    const isPositive = trend.direction === 'up';
    const trendColor = isPositive ? '#1fa678' : '#db3f57';
    const arrow = isPositive ? '↑' : '↓';

    return (
      <MDBox display='flex' alignItems='center' mt={0.75} gap={0.75}>
        <MDBox
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            px: 0.75,
            py: 0.2,
            borderRadius: '6px',
            background: isPositive ? 'rgba(31,166,120,0.1)' : 'rgba(219,63,87,0.1)'
          }}
        >
          <MDTypography variant='caption' fontWeight='bold' sx={{ color: trendColor, fontSize: '0.7rem', lineHeight: 1 }}>
            {arrow} {Math.abs(trend.value)}%
          </MDTypography>
        </MDBox>
        {trend.label && (
          <MDTypography variant='caption' color='text' sx={{ fontSize: '0.68rem', opacity: 0.5 }}>
            {trend.label}
          </MDTypography>
        )}
      </MDBox>
    );
  };

  const renderBreakdown = () => {
    if (!breakdown || breakdown.length === 0) return null;

    return (
      <MDBox mt={1.25}>
        <Tooltip title={breakdownOpen ? 'Hide breakdown' : 'Show breakdown'} placement='top'>
          <MDBox
            display='flex'
            alignItems='center'
            gap={0.5}
            onClick={() => setBreakdownOpen((o) => !o)}
            sx={{
              cursor: 'pointer',
              width: 'fit-content',
              px: 0.875,
              py: 0.3,
              borderRadius: '6px',
              border: `1px solid ${dividerColor}`,
              background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
              userSelect: 'none',
              transition: 'background 0.15s',
              '&:hover': { background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)' }
            }}
          >
            <Icon sx={{ fontSize: '0.8rem !important', color: 'text.secondary', opacity: 0.7 }}>bar_chart</Icon>
            <MDTypography variant='caption' fontWeight='medium' sx={{ fontSize: '0.67rem', color: 'text.secondary' }}>
              {breakdown.length} {breakdown.length === 1 ? 'entry' : 'entries'}
            </MDTypography>
            <Icon sx={{ fontSize: '0.8rem !important', color: 'text.secondary', opacity: 0.6, transition: 'transform 0.18s', transform: breakdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
              expand_more
            </Icon>
          </MDBox>
        </Tooltip>
        <Collapse in={breakdownOpen} timeout={200}>
          <MDBox
            mt={1}
            sx={{
              maxHeight: { xs: 160, sm: 200 },
              overflowY: 'auto',
              borderRadius: '8px',
              border: `1px solid ${dividerColor}`,
              background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'
            }}
          >
            <Table size='small'>
              <TableBody>
                {breakdown.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                      '@media (hover: hover)': { '&:hover': { background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' } }
                    }}
                  >
                    <TableCell sx={{ py: 0.5, px: 1.5, borderBottom: `1px solid ${dividerColor}` }}>
                      <MDTypography variant='caption' fontWeight='regular' sx={{ fontSize: '0.7rem', color: theme.palette.text.secondary, opacity: 0.85 }}>
                        {item.label}
                      </MDTypography>
                    </TableCell>
                    <TableCell align='right' sx={{ py: 0.5, px: 1.5, borderBottom: `1px solid ${dividerColor}` }}>
                      <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.7rem', color: valueColor }}>
                        {item.value}
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </MDBox>
        </Collapse>
      </MDBox>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <MDBox>
          <Skeleton variant='text' width='55%' height={34} sx={{ borderRadius: '4px' }} />
          <Skeleton variant='text' width='38%' height={16} sx={{ mt: 0.5, borderRadius: '4px' }} />
        </MDBox>
      );
    }

    if (placeholder) {
      return (
        <MDBox display='flex' flexDirection='column' py={1}>
          <MDTypography variant='h4' fontWeight='bold' sx={{ fontSize: { xs: '1.35rem', sm: '1.5rem' }, color: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)', letterSpacing: '-0.02em' }}>
            —
          </MDTypography>
          <MDTypography variant='caption' color='text' fontWeight='regular' sx={{ fontSize: '0.7rem', opacity: 0.4, mt: 0.25 }}>
            {placeholderText}
          </MDTypography>
        </MDBox>
      );
    }

    return (
      <>
        <MDTypography
          variant='h4'
          fontWeight='bold'
          sx={{
            lineHeight: 1.15,
            letterSpacing: '-0.025em',
            color: valueColor,
            fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.9rem' }
          }}
        >
          {value}
        </MDTypography>
        {subtitle && (
          <MDTypography variant='caption' color='text' fontWeight='regular' sx={{ fontSize: '0.71rem', opacity: 0.5, mt: 0.2, display: 'block' }}>
            {subtitle}
          </MDTypography>
        )}
        {renderTrend()}
        {renderBreakdown()}
      </>
    );
  };

  return (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '14px',
        boxShadow: 'none',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '@media (hover: hover)': {
          '&:hover': {
            boxShadow: '0 4px 24px rgba(0,0,0,0.09), 0 1px 4px rgba(0,0,0,0.06)',
            transform: 'translateY(-2px)'
          }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: '3px',
          background: barCol,
          borderRadius: '14px 0 0 14px',
          zIndex: 1
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(circle, ${dotColor} 1px, transparent 1px)`,
          backgroundSize: '18px 18px',
          pointerEvents: 'none',
          zIndex: 0
        }
      }}
    >
      {/* Faint watermark icon in background */}
      {icon && (
        <MDBox
          sx={{
            position: 'absolute',
            bottom: '-8px',
            right: '-4px',
            opacity: 0.035,
            pointerEvents: 'none',
            zIndex: 0,
            lineHeight: 1
          }}
        >
          <Icon sx={{ fontSize: '5.5rem !important', color: barCol }}>{icon}</Icon>
        </MDBox>
      )}

      <MDBox sx={{ pl: { xs: 2.25, sm: 2.75 }, pr: { xs: 1.75, sm: 2.25 }, pt: { xs: 1.75, sm: 2 }, pb: 0, position: 'relative', zIndex: 1 }}>
        <MDBox display='flex' justifyContent='space-between' alignItems='center'>
          <MDBox display='flex' alignItems='center'>
            <MDTypography
              variant='caption'
              fontWeight='bold'
              textTransform='uppercase'
              sx={{
                fontSize: '0.6rem',
                letterSpacing: '0.09em',
                color: titleColor
              }}
            >
              {title}
            </MDTypography>
            {tooltip && (
              <Tooltip
                title={tooltip}
                placement='top'
                arrow
                enterTouchDelay={0}
                leaveTouchDelay={3000}
                slotProps={{
                  tooltip: { sx: { maxWidth: 280, fontSize: '0.72rem', lineHeight: 1.5, px: 1.5, py: 1, borderRadius: '8px', background: '#1e293b', color: '#e2e8f0' } },
                  arrow: { sx: { color: '#1e293b' } }
                }}
              >
                <Icon sx={{ fontSize: '0.78rem !important', color: titleColor, opacity: 0.6, ml: 0.5, cursor: 'help', '&:hover': { opacity: 1 } }}>info_outline</Icon>
              </Tooltip>
            )}
          </MDBox>
          {icon && (
            <MDBox
              display='flex'
              justifyContent='center'
              alignItems='center'
              sx={{
                width: { xs: '2rem', sm: '2.1rem' },
                height: { xs: '2rem', sm: '2.1rem' },
                borderRadius: '9px',
                background: iconBg,
                flexShrink: 0
              }}
            >
              <Icon sx={{ color: `${iconCol} !important`, fontSize: '1rem !important' }}>{icon}</Icon>
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      {/* Divider between label and value */}
      <MDBox sx={{ mx: { xs: 2.25, sm: 2.75 }, mt: 1, mb: 0, height: '1px', background: dividerColor, position: 'relative', zIndex: 1 }} />

      <MDBox sx={{ pl: { xs: 2.25, sm: 2.75 }, pr: { xs: 1.75, sm: 2.25 }, pt: 1.25, pb: { xs: 1.75, sm: 2 }, position: 'relative', zIndex: 1 }}>
        {renderContent()}
      </MDBox>

      {/* Bottom accent strip */}
      <MDBox
        sx={{
          position: 'absolute',
          bottom: 0,
          left: '3px',
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, ${barCol}40 0%, transparent 100%)`,
          zIndex: 1
        }}
      />
    </Card>
  );
}

MetricCard.defaultProps = {
  iconColor: 'info',
  loading: false,
  trend: null,
  subtitle: '',
  icon: null,
  placeholder: false,
  placeholderText: 'No data',
  breakdown: null,
  tooltip: ''
};

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subtitle: PropTypes.string,
  icon: PropTypes.string,
  iconColor: PropTypes.oneOf(['primary', 'secondary', 'info', 'success', 'warning', 'error', 'dark']),
  loading: PropTypes.bool,
  trend: PropTypes.shape({
    value: PropTypes.number,
    direction: PropTypes.oneOf(['up', 'down']),
    label: PropTypes.string
  }),
  placeholder: PropTypes.bool,
  placeholderText: PropTypes.string,
  breakdown: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })
  ),
  tooltip: PropTypes.string
};

export default MetricCard;
